import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  ArrowLeft,
  IdCard,
  KeyRound,
  Layers,
  Loader2,
  Mail,
  RotateCcw,
  Save,
  ShieldCheck,
  UserCog,
  UserRound,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { directoryDisplayName, directoryRoleLabel } from "@/components/Directory/roleLabels";
import { mailboxStatusLabel, type DirectoryMailboxStatus } from "@/lib/directoryMailboxLabels";
import {
  AdminStepUpDialog,
  postUserAdminAction,
  type AdminStepUpConfirmResult,
  type AdminStepUpCredentials,
} from "@/components/Users/AdminStepUpDialog";
import { AccessTreeEditor } from "@/components/Access/AccessTreeEditor";
import { apiFetchInit, apiUrl } from "@/lib/apiOrigin";
import {
  DEFAULT_ACCESS_TEMPLATE_ID,
  isAccessTemplateId,
  type AccessTemplateId,
  type ModuleOverrides,
} from "@/lib/accessCatalog";
import type { GlobalAppSlug } from "@/lib/dashboardAppRegistry";
import { hasWorkspaceSystemAccess, sanitizeStoredSystemAccess, type AtwStoredSystemId } from "@/lib/atwSystemAccess";

export type PersonData = {
  org: { orgId: string; domain: string; companyName: string; mailEnabled: boolean };
  person: {
    id: string;
    email: string;
    loginEmail: string | null;
    firstName: string | null;
    lastName: string | null;
    name: string | null;
    jobTitle: string | null;
    createdAt: string;
    lastLoginAt: string | null;
    verificationState: "missing_email" | "invite_pending" | "verified";
    homeDomain: string | null;
    isHomeOrg: boolean;
  };
  access: {
    role: string;
    status: "active" | "suspended";
    appEntitlements: GlobalAppSlug[];
    systemAccess: string[];
    canManageCustomApps?: boolean;
    groupIds: string[];
    templateId?: string;
    overrides?: ModuleOverrides;
  };
  options: { roles: string[]; apps?: Exclude<GlobalAppSlug, "home">[] };
  mailbox: { status: string; email: string | null };
  canEdit?: boolean;
};

type Draft = {
  firstName: string;
  lastName: string;
  jobTitle: string;
  role: string;
  status: "active" | "suspended";
  appEntitlements: Exclude<GlobalAppSlug, "home">[];
  systemAccess: AtwStoredSystemId[];
  canManageCustomApps: boolean;
  groupIds: string[];
  templateId: AccessTemplateId;
  overrides: ModuleOverrides;
};

const TABS = [
  { id: "overview", label: "Overview", icon: IdCard },
  { id: "profile", label: "Profile", icon: UserRound },
  { id: "role", label: "Role & status", icon: UserCog },
  { id: "access", label: "Access", icon: Layers },
  { id: "mail", label: "Mail", icon: Mail },
  { id: "security", label: "Security", icon: ShieldCheck },
] as const;
type TabId = (typeof TABS)[number]["id"];

function draftFrom(d: PersonData): Draft {
  return {
    firstName: d.person.firstName || "",
    lastName: d.person.lastName || "",
    jobTitle: d.person.jobTitle || "",
    role: d.access.role,
    status: d.access.status,
    appEntitlements: (d.access.appEntitlements || []).filter(
      (slug): slug is Exclude<GlobalAppSlug, "home"> => slug !== "home",
    ),
    systemAccess: sanitizeStoredSystemAccess(d.access.systemAccess) ?? [],
    canManageCustomApps: d.access.canManageCustomApps === true,
    groupIds: [...(d.access.groupIds || [])],
    templateId: isAccessTemplateId(d.access.templateId) ? d.access.templateId : DEFAULT_ACCESS_TEMPLATE_ID,
    overrides: d.access.overrides || {},
  };
}

function Section({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-gray-200 bg-white p-5 dark:border-white/10 dark:bg-[#1D232D]">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h2>
      {description ? <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{description}</p> : null}
      <div className="mt-4">{children}</div>
    </section>
  );
}

export function PersonWorkspace({
  personApiPath,
  backHref,
  backLabel,
  mailAdminHref,
}: {
  personApiPath: string;
  backHref: string;
  backLabel: string;
  mailAdminHref: string;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const tab: TabId = TABS.some((t) => t.id === tabParam) ? (tabParam as TabId) : "overview";

  const [data, setData] = useState<PersonData | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey] = useState(0);
  const [saveOpen, setSaveOpen] = useState(false);
  const [quick, setQuick] = useState<"reset_mfa" | "reset_password" | null>(null);

  const personUrl = apiUrl(personApiPath);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setError(null);
      try {
        const r = await fetch(personUrl, { cache: "no-store", ...apiFetchInit() });
        const j = (await r.json()) as PersonData & { error?: string };
        if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
        if (!cancelled) {
          setData(j);
          setDraft(draftFrom(j));
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [personUrl, reloadKey]);

  const dirty = useMemo(
    () => Boolean(data && draft && JSON.stringify(draftFrom(data)) !== JSON.stringify(draft)),
    [data, draft],
  );

  const setTab = (id: TabId) => {
    const next = new URLSearchParams(searchParams);
    if (id === "overview") next.delete("tab");
    else next.set("tab", id);
    setSearchParams(next, { replace: true });
  };

  const save = useCallback(
    async (creds: AdminStepUpCredentials): Promise<AdminStepUpConfirmResult> => {
      if (!draft || !data) return { error: "Nothing to save" };
      const r = await fetch(personUrl, {
        method: "PUT",
        ...apiFetchInit(),
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...draft,
          stepUp: creds,
        }),
      });
      const j = (await r.json()) as PersonData & {
        error?: string;
        needsMfa?: boolean;
        session?: string;
        challengeName?: string;
      };
      if (r.ok && j.needsMfa && j.session && j.challengeName) {
        return { needsMfa: true, session: j.session, challengeName: j.challengeName };
      }
      if (!r.ok) return { error: j.error || "Could not save" };
      setData(j);
      setDraft(draftFrom(j));
      toast.success("Saved.");
      return { ok: true };
    },
    [personUrl, draft, data],
  );

  const runQuick = useCallback(
    async (creds: AdminStepUpCredentials): Promise<AdminStepUpConfirmResult> => {
      if (!quick || !data) return { error: "No action" };
      const result = await postUserAdminAction(data.person.id, quick, creds);
      if ("needsMfa" in result && result.needsMfa) return result;
      if ("error" in result) return result;
      toast.success(quick === "reset_mfa" ? "MFA reset." : "Password reset email sent.");
      return { ok: true };
    },
    [quick, data],
  );

  if (error) {
    return (
      <div className="space-y-4">
        <Link to={backHref} className="text-sm text-gray-600 hover:underline dark:text-gray-400">
          ← {backLabel}
        </Link>
        <p className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900">{error}</p>
      </div>
    );
  }

  if (!data || !draft) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="size-6 animate-spin text-gray-400" aria-label="Loading" />
      </div>
    );
  }

  const { org, person, access, options } = data;
  const canEdit = data.canEdit !== false;
  const isSuperAdminTarget = access.role === "super_admin";
  const locked = !canEdit || isSuperAdminTarget;
  const ownerApps = draft.role === "account_owner" || draft.role === "super_admin";
  const set = (patch: Partial<Draft>) => setDraft({ ...draft, ...patch });
  const name = directoryDisplayName(person);
  const mailGranted = hasWorkspaceSystemAccess(draft.systemAccess);

  return (
    <div className="flex flex-col gap-5 pb-24">
      <div className="flex items-start gap-3">
        <Link
          to={backHref}
          className="inline-flex size-9 items-center justify-center rounded-md hover:bg-gray-100 dark:hover:bg-white/10"
          aria-label={`Back to ${backLabel}`}
        >
          <ArrowLeft className="size-5" />
        </Link>
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">{name}</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            {person.email || "no email"} · {org.companyName}
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <Badge variant="outline">{directoryRoleLabel(access.role)}</Badge>
            <Badge variant={access.status === "active" ? "default" : "secondary"}>
              {access.status === "active" ? "Active" : "Suspended"}
            </Badge>
          </div>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-[13rem_1fr]">
        <nav className="flex gap-1 overflow-x-auto md:flex-col" aria-label="Person sections">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              className={`inline-flex items-center gap-2 whitespace-nowrap rounded-md px-3 py-2 text-left text-sm font-medium ${
                tab === id
                  ? "bg-gray-900 text-white dark:bg-white dark:text-gray-900"
                  : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/10"
              }`}
            >
              <Icon className="size-4" aria-hidden />
              {label}
            </button>
          ))}
        </nav>

        <div className="min-w-0 space-y-5">
          {tab === "overview" ? (
            <Section title="Identity">
              <p className="text-sm">
                <span className="text-gray-500">User ID:</span>{" "}
                <span className="font-mono text-xs">{person.id}</span>
              </p>
              <p className="mt-2 text-sm">
                <span className="text-gray-500">Last sign-in:</span>{" "}
                {person.lastLoginAt ? new Date(person.lastLoginAt).toLocaleString() : "Never"}
              </p>
              <p className="mt-2 text-sm">
                <span className="text-gray-500">Mailbox:</span>{" "}
                {mailGranted
                  ? mailboxStatusLabel((data.mailbox.status || "none") as DirectoryMailboxStatus)
                  : "No Mail access"}
              </p>
            </Section>
          ) : null}

          {tab === "profile" ? (
            <Section title="Profile">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="firstName">First name</Label>
                  <Input
                    id="firstName"
                    value={draft.firstName}
                    disabled={!canEdit}
                    onChange={(e) => set({ firstName: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="lastName">Last name</Label>
                  <Input
                    id="lastName"
                    value={draft.lastName}
                    disabled={!canEdit}
                    onChange={(e) => set({ lastName: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="jobTitle">Job title</Label>
                  <Input
                    id="jobTitle"
                    value={draft.jobTitle}
                    disabled={!canEdit}
                    onChange={(e) => set({ jobTitle: e.target.value })}
                  />
                </div>
              </div>
            </Section>
          ) : null}

          {tab === "role" ? (
            <Section title="Role & status">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="role">Role</Label>
                  <select
                    id="role"
                    className="h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm dark:border-white/15 dark:bg-[#12161d]"
                    value={draft.role}
                    disabled={locked}
                    onChange={(e) => set({ role: e.target.value })}
                  >
                    {data.options.roles.map((r) => (
                      <option key={r} value={r}>
                        {directoryRoleLabel(r)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label>Status</Label>
                  <div className="flex h-10 items-center justify-between rounded-md border px-3 dark:border-white/15">
                    <span className="text-sm">{draft.status === "active" ? "Active" : "Suspended"}</span>
                    <Switch
                      checked={draft.status === "active"}
                      onCheckedChange={(on) => set({ status: on ? "active" : "suspended" })}
                      disabled={locked}
                    />
                  </div>
                </div>
              </div>
            </Section>
          ) : null}

          {tab === "access" ? (
            <Section
              title="Access"
              description="Systems they can sign in to, the applications inside each, and what they can do in every module."
            >
              <div className="space-y-4">
                <AccessTreeEditor
                  value={{
                    systemAccess: draft.systemAccess,
                    apps: draft.appEntitlements,
                    templateId: draft.templateId,
                    overrides: draft.overrides,
                  }}
                  onChange={
                    locked
                      ? undefined
                      : (next) =>
                          set({
                            systemAccess: next.systemAccess,
                            appEntitlements: next.apps,
                            templateId: next.templateId,
                            overrides: next.overrides,
                          })
                  }
                  assignableAppSlugs={options.apps || []}
                  fullAccessLocked={ownerApps}
                  mailAvailable={org.mailEnabled}
                  readOnly={locked}
                />
                <div className="flex items-center justify-between gap-4 rounded-lg border p-4 dark:border-white/10">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Can manage custom apps</p>
                    <p className="mt-1 text-xs text-gray-500">Create and edit org-owned custom apps.</p>
                  </div>
                  <Switch
                    checked={ownerApps || draft.canManageCustomApps}
                    onCheckedChange={(on) => set({ canManageCustomApps: on })}
                    disabled={ownerApps || locked}
                    aria-label="Can manage custom apps"
                  />
                </div>
              </div>
            </Section>
          ) : null}

          {tab === "mail" ? (
            <Section title="Mail">
              {!org.mailEnabled ? (
                <p className="text-sm text-gray-600">Mail is not enabled for this org.</p>
              ) : (
                <>
                  <p className="text-sm">{data.mailbox.email || person.loginEmail || person.email || "—"}</p>
                  <p className="mt-2 text-sm text-gray-600">
                    {mailboxStatusLabel((data.mailbox.status || "none") as DirectoryMailboxStatus)}
                  </p>
                  <a
                    href={mailAdminHref}
                    className="mt-4 inline-flex rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium hover:bg-gray-50 dark:border-white/20"
                  >
                    Open Mail admin
                  </a>
                </>
              )}
            </Section>
          ) : null}

          {tab === "security" ? (
            <Section title="Security">
              {canEdit && person.verificationState === "verified" ? (
                <div className="flex flex-wrap gap-2">
                  <Button type="button" variant="outline" onClick={() => setQuick("reset_mfa")}>
                    <ShieldCheck className="mr-2 size-4" /> Reset MFA
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setQuick("reset_password")}>
                    <KeyRound className="mr-2 size-4" /> Reset password
                  </Button>
                </div>
              ) : (
                <p className="text-sm text-gray-600">Resend invite from the People list for pending users.</p>
              )}
            </Section>
          ) : null}
        </div>
      </div>

      {dirty ? (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t bg-white/95 px-4 py-3 shadow-lg backdrop-blur dark:bg-[#1D232D]/95">
          <div className="mx-auto flex max-w-6xl items-center justify-between gap-3">
            <span className="text-sm text-gray-600">Unsaved changes</span>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setDraft(draftFrom(data))}>
                <RotateCcw className="mr-2 size-4" /> Discard
              </Button>
              <Button type="button" onClick={() => setSaveOpen(true)}>
                <Save className="mr-2 size-4" /> Save
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {saveOpen ? (
        <AdminStepUpDialog
          open
          onOpenChange={setSaveOpen}
          title="Save changes"
          description={`Confirm with your password to update ${person.email}.`}
          confirmLabel="Save"
          onConfirm={save}
        />
      ) : null}

      {quick ? (
        <AdminStepUpDialog
          open
          onOpenChange={(open) => {
            if (!open) setQuick(null);
          }}
          title={quick === "reset_mfa" ? "Reset MFA" : "Reset password"}
          description="Confirm with your password (and MFA if prompted)."
          confirmLabel="Confirm"
          onConfirm={runQuick}
        />
      ) : null}
    </div>
  );
}
